import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { paths } from "@shared-core/routing/routes";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../app/components/primitives/tabs";
import { BrandHubTab } from "./BrandHubTab";
import { ConsoleClockHeader } from "./ConsoleClockHeader";
import { InboundMailTab } from "./InboundMailTab";
import { JobsTab } from "./JobsTab";
import { OutboxTab } from "./OutboxTab";
import { ScenariosTab } from "./ScenariosTab";
import { SuppliersTab } from "./SuppliersTab";
import { XeroTab } from "./XeroTab";

/** The console's tabs, in order; Scenarios is the default (07 §1). */
export const CONSOLE_TABS = [
  { key: "scenarios", label: "Scenarios" },
  { key: "outbox", label: "Outbox" },
  { key: "suppliers", label: "Suppliers" },
  { key: "xero", label: "Xero" },
  { key: "brandhub", label: "BrandHub" },
  { key: "inbound-mail", label: "Inbound mail" },
  { key: "jobs", label: "Jobs" },
] as const;
type ConsoleTab = (typeof CONSOLE_TABS)[number]["key"];

function isConsoleTab(value: string | undefined): value is ConsoleTab {
  return CONSOLE_TABS.some((tab) => tab.key === value);
}

/**
 * The test console (docs/features/quote-to-invoice-e2e-testing/07 §1): a testing preview's scenarios, outbox and
 * simulated outside parties, under one clock. Reached only in a testing preview (the `journey_testing` feature)
 * by the roles holding journey_testing:use; everything a tester does on the real screens is still their own.
 */
export function JourneyTestingConsolePage() {
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const active: ConsoleTab = isConsoleTab(tab) ? tab : "scenarios";
  // A fast-forward runs the jobs and can send email or move documents: re-read the open tab after one.
  const [generation, setGeneration] = useState(0);

  return (
    <div className="space-y-6 p-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">Test console</h1>
        <p className="text-muted-foreground">
          Set a journey up, play the customer's suppliers, Xero and BrandHub,
          and move time on. Nothing leaves this preview: its email is in the
          outbox.
        </p>
      </header>
      <ConsoleClockHeader onAdvanced={() => setGeneration((g) => g + 1)} />
      <Tabs
        value={active}
        onValueChange={(value) =>
          navigate(paths.journeyTesting({ tab: value }))
        }
      >
        <TabsList className="flex-wrap">
          {CONSOLE_TABS.map((t) => (
            <TabsTrigger key={t.key} value={t.key}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <div key={generation} className="pt-4">
          <TabsContent value="scenarios">
            <ScenariosTab />
          </TabsContent>
          <TabsContent value="outbox">
            <OutboxTab />
          </TabsContent>
          <TabsContent value="suppliers">
            <SuppliersTab />
          </TabsContent>
          <TabsContent value="xero">
            <XeroTab />
          </TabsContent>
          <TabsContent value="brandhub">
            <BrandHubTab />
          </TabsContent>
          <TabsContent value="inbound-mail">
            <InboundMailTab />
          </TabsContent>
          <TabsContent value="jobs">
            <JobsTab />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
